import type { Ad, Plan } from "@1sky/shared";
import { useEffect, useMemo, useRef, useState } from "react";
import { config } from "../lib/config";
import { hubApi } from "../lib/hub";
import { scoreAds } from "./scoreAds";

const ROTATE_MS = 90_000;
const VIEWABLE_RATIO = 0.5;
const VIEWABLE_MS = 1_000;

let inventory: Promise<Ad[]> | null = null;
function loadAds(): Promise<Ad[]> {
  inventory ??= hubApi
    .call<{ ads: Ad[] }>("/api/ads")
    .then((r) => r.ads)
    .catch(() => []);
  return inventory;
}
export function resetAdCache() {
  inventory = null;
}

interface Props {
  plan?: Plan;
  lang: string;
  recentText: string[];
  placement: "call" | "landing";
}

/** One discreet, clearly labelled sponsored card. Never rendered for Pro. */
export function AdSlot({ plan = "free", ...rest }: Props) {
  if (plan === "pro") return null;
  return <FreeAdSlot {...rest} />;
}

function FreeAdSlot({ lang, recentText, placement }: Omit<Props, "plan">) {
  const [ads, setAds] = useState<Ad[]>([]);
  const [hidden, setHidden] = useState(false);
  const [tick, setTick] = useState(0);
  const el = useRef<HTMLDivElement>(null);
  const logged = useRef(new Set<string>());

  useEffect(() => {
    let alive = true;
    void loadAds().then((a) => alive && setAds(a));
    const t = setInterval(() => setTick((n) => n + 1), ROTATE_MS);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);

  // Re-pick only on rotation, not on every transcript line, so the card doesn't flicker.
  const textRef = useRef(recentText);
  textRef.current = recentText;
  const ad = useMemo(() => scoreAds(ads, { lang, recentText: textRef.current }), [ads, lang, tick]);
  const directAd = ad && ad.kind === "direct" ? ad : null;
  const useAdsense = !!config.adsenseClient && !directAd;

  useEffect(() => {
    const node = el.current;
    if (!node || !ad || useAdsense || typeof IntersectionObserver === "undefined") return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const obs = new IntersectionObserver(
      ([entry]) => {
        clearTimeout(timer);
        if (!entry || entry.intersectionRatio < VIEWABLE_RATIO || logged.current.has(ad.id)) return;
        timer = setTimeout(() => {
          logged.current.add(ad.id);
          void hubApi.call("/api/ads/event", { method: "POST", body: JSON.stringify({ adId: ad.id, type: "impression", lang }) }).catch(() => undefined);
        }, VIEWABLE_MS);
      },
      { threshold: [0, VIEWABLE_RATIO, 1] },
    );
    obs.observe(node);
    return () => {
      clearTimeout(timer);
      obs.disconnect();
    };
  }, [ad, lang, useAdsense]);

  if (hidden) return null;
  if (useAdsense) return <AdsenseUnit placement={placement} />;
  if (!ad) return null;

  const href = `${config.hubUrl}/api/ads/click/${encodeURIComponent(ad.id)}?lang=${encodeURIComponent(lang)}`;
  return (
    <div ref={el} className={placement === "call" ? "border-t border-line p-3" : ""}>
      <div className="group relative rounded-2xl bg-soft p-4">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-muted">Sponsored</span>
          <button
            aria-label="Hide this ad"
            onClick={() => setHidden(true)}
            className="-m-1 flex h-7 w-7 cursor-pointer items-center justify-center rounded-full text-muted opacity-70 hover:bg-line hover:opacity-100"
          >
            <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden>
              <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
            </svg>
          </button>
        </div>
        <a href={href} target="_blank" rel="sponsored noopener" className="mt-1 flex gap-3">
          {ad.imageUrl && <img src={ad.imageUrl} alt="" className="h-10 w-10 shrink-0 rounded-lg object-cover" loading="lazy" />}
          <span className="min-w-0">
            <span className="block text-xs text-muted">{ad.advertiser}</span>
            <span className="block text-sm leading-snug font-semibold text-ink">{ad.headline}</span>
            <span className="mt-0.5 block text-sm leading-snug text-muted">{ad.body}</span>
            <span className="mt-2 inline-block text-sm font-semibold text-ink underline underline-offset-2">{ad.cta}</span>
          </span>
        </a>
      </div>
    </div>
  );
}

/** Optional network fallback when no direct-sold ad matches. Loads AdSense lazily. */
function AdsenseUnit({ placement }: { placement: "call" | "landing" }) {
  const ref = useRef<HTMLModElement>(null);
  useEffect(() => {
    const id = "adsbygoogle-js";
    if (!document.getElementById(id)) {
      const s = document.createElement("script");
      s.id = id;
      s.async = true;
      s.crossOrigin = "anonymous";
      s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${encodeURIComponent(config.adsenseClient)}`;
      document.head.appendChild(s);
    }
    try {
      ((window as unknown as { adsbygoogle: unknown[] }).adsbygoogle ||= []).push({});
    } catch {
      /* blocked by an ad blocker */
    }
  }, []);
  return (
    <div className={placement === "call" ? "border-t border-line p-3" : ""}>
      <span className="text-xs font-medium text-muted">Sponsored</span>
      <ins ref={ref} className="adsbygoogle block" style={{ display: "block" }} data-ad-client={config.adsenseClient} data-ad-format="fluid" data-full-width-responsive="true" />
    </div>
  );
}
